import React, { CSSProperties } from 'react';
import { connect } from 'react-redux';
import { injectIntl, defineMessages } from 'react-intl';
import Logger from 'js-logger';
import { mediaSelectors, streamActions } from '@lifesize/nucleus';
import actions from 'actions/InCall/InCallVideo';
import { VIDEO_SECONDARY } from 'constants/InCall/InCallVideo';
import WindowPortal from './WindowPortal';

const messages = defineMessages({
  popoutTitle: { id: 'components.callVideo.popoutTitle', defaultMessage: 'Lifesize Presentation' }
});

// Note: there are no stylesheets passed to the portal, so style the elements directly
const containerStyle: CSSProperties = {
  alignItems: 'center',
  backgroundColor: 'black',
  bottom: 0,
  display: 'flex',
  height: '100%',
  justifyContent: 'center',
  left: 0,
  position: 'absolute',
  right: 0,
  top: 0,
  width: '100%'
};
const videoStyle: CSSProperties = {
  height: 'auto',
  maxHeight: '100vh',
  maxWidth: '100vw',
  minHeight: '100%',
  minWidth: '100vw',
  width: 'auto'
};

interface Props {
  intl: any;
  onPlaybackFailure: Function;
  onPlaybackSuccess: Function;
  remotePresentationStream: any;
  togglePopout: Function;
}

class Popout extends React.PureComponent<Props> {
  videoElement: HTMLVideoElement | null;

  constructor(props) {
    super(props);

    this.videoElement = null;
  }
  attachRemoteStreamToPopOut(mediaStream: any) {
    if (this.videoElement && mediaStream) {
      if (this.videoElement.srcObject !== mediaStream) {
        this.videoElement.srcObject = mediaStream;
      }
      // this.videoElement.oncanplay = this.playVideo; // Note: this does not fire on subsequent openings of the popout
      setTimeout(this.playVideo, 0);
    }
  }

  componentDidMount() {
    const { remotePresentationStream: mediaStream } = this.props;
    this.attachRemoteStreamToPopOut(mediaStream);
  }

  UNSAFE_componentWillReceiveProps(nextProps: Readonly<Props>) {
    // update remote mediastream if it is changed.
    const { remotePresentationStream: mediaStream } = nextProps;
    if (this.props.remotePresentationStream.id !== mediaStream.id) {
      this.attachRemoteStreamToPopOut(mediaStream);
    }
  }

  componentWillUnmount() {
    // IMPORTANT: Need to close popout presentation from parent window because when presentation is stopped from remote,
    // portal content will be erased and parent component will not be able to talk to child component.
    // @ts-ignore
    window.popoutWindowHandle.close();
    // @ts-ignore
    window.popoutWindowHandle = null;
  }

  playVideo = async () => {
    if (this.videoElement) {
      // to satisfy typescript
      try {
        await this.videoElement.play(); // wait for the promise
        this.props.onPlaybackSuccess(this.videoElement);
      } catch (e) {
        this.props.onPlaybackFailure(this.videoElement, e);
      }
    }
  };

  onClosePopout = () => {
    this.props.togglePopout(false);
  };

  render() {
    return (
      <WindowPortal
        title={this.props.intl.formatMessage(messages.popoutTitle)}
        onClosePopout={this.onClosePopout}
        onPopoutBlocked={() => Logger.warn('Popout Blocked')} // TODO: show toast message toi user
      >
        <div style={containerStyle}>
          <video
            id={VIDEO_SECONDARY}
            muted={true} // must be muted to prevent "play() failed because the user didn't interact with the document first."
            playsInline
            ref={(elt) => {
              this.videoElement = elt;
            }}
            style={videoStyle}
          />
        </div>
      </WindowPortal>
    );
  }
}

const mapDispatchToProps = (dispatch) => ({
  onPlaybackFailure: (elt, error) => {
    dispatch(streamActions.videoBlocked(elt));
    Logger.warn('Popout playback failure', { error });
  },
  onPlaybackSuccess: (elt) => {
    dispatch(streamActions.videoPlaying(elt));
    Logger.info('Popout playback success');
  },
  togglePopout: (toggle) => dispatch(actions.togglePopout(toggle))
});

const mapStateToProps = (state) => {
  // @ts-ignore
  const remotePresentationStream = mediaSelectors.selectMedia(state).remotePresentationStream;

  return {
    remotePresentationStream
  };
};

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(Popout));
